import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

/**
 * Tracks which section walkthroughs a user has already seen (so the first-run
 * tour auto-opens exactly once) and which tour, if any, is currently playing.
 *
 * Seen-state persists in localStorage under `dca_help_seen`; the active tour
 * is in-memory only. Mounted once in AppLayout so every authenticated page and
 * the WalkthroughHost share the same state.
 */

const STORAGE_KEY = 'dca_help_seen'

function readSeen(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return {}
    const parsed: unknown = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, boolean>) : {}
  } catch {
    return {}
  }
}

function writeSeen(map: Record<string, boolean>): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map))
  } catch {
    /* storage unavailable (private mode); tours simply re-show, no crash */
  }
}

type HelpContextValue = {
  readonly activeTour: string | null
  readonly startTour: (key: string) => void
  readonly closeTour: () => void
  readonly isFirstRun: (key: string) => boolean
  readonly markSeen: (key: string) => void
}

const HelpContext = createContext<HelpContextValue | null>(null)

export function HelpProvider({ children }: { readonly children: ReactNode }) {
  const [seen, setSeen] = useState<Record<string, boolean>>(readSeen)
  const [activeTour, setActiveTour] = useState<string | null>(null)

  const markSeen = useCallback((key: string) => {
    setSeen((prev) => {
      if (prev[key]) return prev
      const next = { ...prev, [key]: true }
      writeSeen(next)
      return next
    })
  }, [])

  const isFirstRun = useCallback((key: string) => !seen[key], [seen])
  const startTour = useCallback((key: string) => setActiveTour(key), [])
  const closeTour = useCallback(() => setActiveTour(null), [])

  const value = useMemo<HelpContextValue>(
    () => ({ activeTour, startTour, closeTour, isFirstRun, markSeen }),
    [activeTour, startTour, closeTour, isFirstRun, markSeen],
  )

  return <HelpContext.Provider value={value}>{children}</HelpContext.Provider>
}

export function useHelp(): HelpContextValue {
  const ctx = useContext(HelpContext)
  if (!ctx) throw new Error('useHelp must be used within a HelpProvider')
  return ctx
}
